import {
  Alert,
  Button,
  Descriptions,
  Modal,
  Spin,
  Tag,
  Typography,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import formatarDuracao from "../../utils/formatarDuracao";
import useApi from "../../utils/useApi";
import { FaixaDeBarras, Gadget } from "./Gadgets";
import {
  AVISOS,
  LIMITE_KBPS_RECOMENDADO,
  diaEHora,
  estadoDaCamera,
  faixasDeSegmentos,
  haQuantoTempo,
  hora,
  mascararEndereco,
  textoDaDecisao,
} from "./textos";

const { Text } = Typography;

function megabytes(bytes) {
  return `${(bytes / 1048576).toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
  })} MB`;
}

function MedidorDeBitrate({ kbps }) {
  const escala = Math.max(3000, kbps || 0);
  const pesado = kbps > LIMITE_KBPS_RECOMENDADO;
  return (
    <div
      style={{ position: "relative", height: 16, marginTop: 4 }}
      title={`${(kbps || 0).toLocaleString("pt-BR")} kbps`}>
      <div
        style={{
          position: "absolute",
          top: 4,
          left: 0,
          right: 0,
          height: 8,
          borderRadius: 4,
          background: "#e6f4ff",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 4,
          left: 0,
          width: `${((kbps || 0) / escala) * 100}%`,
          height: 8,
          borderRadius: 4,
          background: pesado ? "#fa8c16" : "#1677ff",
        }}
      />
      <div
        title="Recomendado até 1.000 kbps"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: `${(LIMITE_KBPS_RECOMENDADO / escala) * 100}%`,
          borderLeft: "1px dashed rgba(0,0,0,.45)",
        }}
      />
    </div>
  );
}

export default function CameraModal({
  filialId,
  cameraId,
  onFechar,
  onEditar,
  onDecidir,
  decidindo,
  aguardando,
}) {
  const { getGatewayCamera } = useApi();
  const { data: camera, isLoading } = useQuery({
    queryKey: ["gateway-camera", filialId, cameraId],
    queryFn: () => getGatewayCamera(filialId, cameraId),
    enabled: Boolean(cameraId),
    refetchInterval: 15000,
  });
  const agora = new Date();
  const reportada = camera?.reportada;
  const estado = estadoDaCamera(reportada);
  const kbps = reportada?.kbpsMedido;
  const parada = camera?.gravacao?.parada;

  return (
    <Modal
      open={Boolean(cameraId)}
      width={880}
      title={
        camera && (
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {camera.nome}
            <Tag color={estado.cor}>{estado.rotulo}</Tag>
          </span>
        )
      }
      onCancel={onFechar}
      footer={[
        <Button
          key="editar"
          onClick={() => onEditar(camera)}
          disabled={!camera}>
          Editar câmera
        </Button>,
        <Button key="fechar" type="primary" onClick={onFechar}>
          Fechar
        </Button>,
      ]}>
      {isLoading || !camera ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Text type="secondary">
            {reportada?.desde
              ? `${estado.rotulo} desde ${diaEHora(reportada.desde, agora)}`
              : "O gateway ainda não reportou esta câmera."}
          </Text>
          {estado.frase && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 10 }}
              message={estado.frase}
              description={
                reportada?.ultimoErro
                  ? `Código: ${reportada.estado} · ${reportada.ultimoErro}`
                  : `Código: ${reportada.estado}`
              }
            />
          )}
          {(reportada?.avisos || []).map((aviso) => (
            <Alert
              key={aviso}
              type="warning"
              showIcon
              style={{ marginTop: 10 }}
              message={AVISOS[aviso] || aviso}
            />
          ))}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
              margin: "14px 0",
            }}>
            <Gadget
              rotulo="Último segmento"
              valor={haQuantoTempo(camera.ultimoSegmento?.fim, agora)}
              rodape={
                camera.ultimoSegmento
                  ? `${hora(camera.ultimoSegmento.inicio)} · ${formatarDuracao(
                      Math.round(camera.ultimoSegmento.duracaoMs / 60000)
                    )} · ${megabytes(camera.ultimoSegmento.tamanhoBytes)}`
                  : "Nenhum vídeo recebido"
              }
            />
            <Gadget
              rotulo="Vídeo"
              valor={
                reportada?.largura
                  ? `${reportada.largura}x${reportada.altura}`
                  : "-"
              }
              rodape={
                reportada?.codec
                  ? `${reportada.codec}${
                      reportada.fpsMedido ? ` · ${reportada.fpsMedido} fps` : ""
                    }`
                  : "-"
              }
            />
            <Gadget
              rotulo="Bitrate"
              valor={kbps ? kbps.toLocaleString("pt-BR") : "-"}
              unidade={kbps ? "kbps" : undefined}
              aviso={kbps > LIMITE_KBPS_RECOMENDADO}
              rodape="Recomendado até 1.000 kbps">
              {kbps > 0 && <MedidorDeBitrate kbps={kbps} />}
            </Gadget>
            <Gadget
              rotulo="Enviados hoje"
              valor={camera.enviadosHoje}
              rodape="segmentos recebidos pelo Ouran"
            />
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Segmentos recebidos nas últimas 6 horas
          </Text>
          <div style={{ margin: "4px 0 14px" }}>
            <FaixaDeBarras
              faixas={faixasDeSegmentos(camera.segmentos6h, agora)}
              cor="#1677ff"
              dica={(f) =>
                `${hora(f.em)} · ${
                  f.recebido ? "segmento recebido" : "sem segmento"
                }`
              }
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "#fafafa",
              borderRadius: 6,
              padding: "10px 12px",
              marginBottom: 12,
            }}>
            <div style={{ flex: 1 }}>
              <Text strong>Gravação desta câmera</Text>
              <div>
                <Text type="secondary">
                  {textoDaDecisao(camera.gravacao, agora)}
                </Text>
              </div>
              {aguardando && (
                <Text type="warning" style={{ fontSize: 13 }}>
                  Solicitado, aguardando o gateway (até 60 s)
                </Text>
              )}
            </div>
            <Button
              danger={!parada}
              loading={decidindo}
              onClick={() => onDecidir(camera, !parada)}>
              {parada ? "Retomar" : "Parar"}
            </Button>
          </div>
          <Descriptions column={1} size="small" labelStyle={{ width: 180 }}>
            <Descriptions.Item label="Endereço">
              <Text code>{mascararEndereco(camera.endereco)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Re-encode">
              {camera.reencode ? "Sim" : "Não"}
            </Descriptions.Item>
            <Descriptions.Item label="Conexão com a fonte">
              {reportada?.conectada === true
                ? "Conectada"
                : reportada?.conectada === false
                  ? "Desconectada"
                  : "-"}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Modal>
  );
}
